import React, { useState, useRef } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Textarea from '@mui/joy/Textarea';
import IconButton from '@mui/joy/IconButton';
import Dropdown from '@mui/joy/Dropdown';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import Alert from '@mui/joy/Alert';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import InfoIcon from '@mui/icons-material/Info';
import VoiceRecorder, { VoiceRecorderPhase } from './VoiceRecorder';
import ImagePreview from './ImagePreview';
import LocationPicker from './LocationPicker';
import StickerPicker from './StickerPicker';

interface ComposerBarProps {
  onSendText: (message: string) => Promise<void>;
  onSendImage: (file: File, caption?: string) => Promise<void>;
  onSendAudio: (blob: Blob, duration: string) => Promise<void>;
  onSendLocation: (latitude: number, longitude: number, name?: string) => Promise<void>;
  onSendSticker: (emoji: string) => Promise<void>;
  isLoading: boolean;
  whatsappAvailable: boolean;
  channel?: 'meta' | 'whatsapp_web';
}

const ComposerBar: React.FC<ComposerBarProps> = ({
  onSendText,
  onSendImage,
  onSendAudio,
  onSendLocation,
  onSendSticker,
  isLoading,
  whatsappAvailable,
  channel = 'meta',
}) => {
  const isReadOnly = channel === 'whatsapp_web';
  const canSend = whatsappAvailable && !isLoading && !isReadOnly;
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [previewAudioUrl, setPreviewAudioUrl] = useState<string | null>(null);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showStickerPicker, setShowStickerPicker] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingDurationMsRef = useRef(0);
  const previewBlobRef = useRef<Blob | null>(null);

  const startRecording = async () => {
    if (!canSend) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = 'audio/ogg;codecs=opus';
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported(mimeType) ? mimeType : 'audio/webm',
      });

      recordingChunksRef.current = [];
      recordingDurationMsRef.current = 0;
      setRecordingDuration(0);
      setIsRecording(true);

      mediaRecorder.ondataavailable = (event) => {
        recordingChunksRef.current.push(event.data);
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;

      recordingIntervalRef.current = setInterval(() => {
        recordingDurationMsRef.current += 100;
        setRecordingDuration(recordingDurationMsRef.current);
      }, 100);
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const finishRecording = (): void => {
    const mr = mediaRecorderRef.current;
    if (!mr || !isRecording) return;
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    mr.onstop = () => {
      const blob = new Blob(recordingChunksRef.current, { type: mr.mimeType });
      previewBlobRef.current = blob;
      const url = URL.createObjectURL(blob);
      setPreviewAudioUrl(url);
      mr.stream?.getTracks().forEach((t) => t.stop());
      mediaRecorderRef.current = null;
    };
    mr.stop();
    setIsRecording(false);
  };

  const discardWhileRecording = (): void => {
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
    const mr = mediaRecorderRef.current;
    if (mr) {
      if (mr.state === 'recording') {
        mr.onstop = () => {
          mr.stream?.getTracks().forEach((t) => t.stop());
          mediaRecorderRef.current = null;
        };
        mr.stop();
      } else {
        mr.stream?.getTracks().forEach((t) => t.stop());
        mediaRecorderRef.current = null;
      }
    }
    setIsRecording(false);
    recordingDurationMsRef.current = 0;
    setRecordingDuration(0);
    recordingChunksRef.current = [];
  };

  const sendRecording = async (): Promise<void> => {
    const blob = previewBlobRef.current;
    if (!blob) return;
    const urlToRevoke = previewAudioUrl;
    const duration = `${Math.floor(recordingDuration / 1000)}`;
    await onSendAudio(blob, duration);
    if (urlToRevoke) {
      URL.revokeObjectURL(urlToRevoke);
    }
    setPreviewAudioUrl(null);
    previewBlobRef.current = null;
    recordingDurationMsRef.current = 0;
    setRecordingDuration(0);
    recordingChunksRef.current = [];
  };

  const discardPreview = (): void => {
    if (previewAudioUrl) {
      URL.revokeObjectURL(previewAudioUrl);
    }
    setPreviewAudioUrl(null);
    previewBlobRef.current = null;
    recordingDurationMsRef.current = 0;
    setRecordingDuration(0);
    recordingChunksRef.current = [];
  };

  const voicePhase: VoiceRecorderPhase = ((): VoiceRecorderPhase => {
    if (isRecording) return 'recording';
    if (previewAudioUrl) return 'preview';
    return 'idle';
  })();

  const handleSendText = async () => {
    if (message.trim()) {
      await onSendText(message.trim());
      setMessage('');
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPreviewFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreviewImage(event.target?.result as string);
        setShowImagePreview(true);
      };
      reader.readAsDataURL(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImagePreviewConfirm = async (caption: string) => {
    if (previewFile) {
      await onSendImage(previewFile, caption || undefined);
      setShowImagePreview(false);
      setPreviewImage('');
      setPreviewFile(null);
    }
  };

  const handleLocationConfirm = async (
    latitude: number,
    longitude: number,
    name?: string,
  ) => {
    await onSendLocation(latitude, longitude, name);
    setShowLocationPicker(false);
  };

  const handleStickerSelect = async (emoji: string) => {
    await onSendSticker(emoji);
  };

  return (
    <Sheet
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 0,
        borderLeft: 'none',
        borderRight: 'none',
        borderBottom: 'none',
      }}
    >
      {isReadOnly && (
        <Alert
          color="warning"
          startDecorator={<InfoIcon />}
          sx={{ mb: 2 }}
        >
          Esta conexión es de solo lectura. No puedes enviar mensajes desde aquí.
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          style={{ display: 'none' }}
        />

        <Dropdown>
          <MenuButton
            slots={{ root: IconButton }}
            slotProps={{ root: { size: 'sm', variant: 'plain', disabled: !canSend } }}
          >
            <AttachFileIcon />
          </MenuButton>
          <Menu placement="top-start" size="sm">
            <MenuItem
              onClick={() => {
                fileInputRef.current?.click();
              }}
            >
              <ImageOutlinedIcon sx={{ mr: 1 }} />
              Imagen
            </MenuItem>
            <MenuItem
              onClick={() => {
                setShowLocationPicker(true);
              }}
            >
              <LocationOnIcon sx={{ mr: 1 }} />
              Ubicación
            </MenuItem>
            <MenuItem
              onClick={() => {
                setShowStickerPicker(true);
              }}
            >
              <EmojiEmotionsOutlinedIcon sx={{ mr: 1 }} />
              Sticker
            </MenuItem>
          </Menu>
        </Dropdown>

        <Textarea
          placeholder={
            isReadOnly
              ? 'Solo lectura — no puedes enviar mensajes'
              : whatsappAvailable
                ? 'Escribir mensaje...'
                : 'WhatsApp no disponible — no podés enviar mensajes'
          }
          value={message}
          disabled={!canSend}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && message.trim() && canSend) {
              e.preventDefault();
              handleSendText();
            }
          }}
          minRows={1}
          maxRows={4}
          sx={{ flex: 1, minWidth: 0, '--Textarea-radius': '12px' }}
        />

        <IconButton
          color="primary"
          disabled={!message.trim() || !canSend}
          onClick={handleSendText}
        >
          <SendIcon />
        </IconButton>

        <VoiceRecorder
          phase={voicePhase}
          durationMs={recordingDuration}
          previewAudioUrl={previewAudioUrl}
          onStartRecording={startRecording}
          onFinishRecording={finishRecording}
          onDiscardRecording={discardWhileRecording}
          onSendRecording={sendRecording}
          onDiscardPreview={discardPreview}
          disabled={!canSend}
          isSending={isLoading}
        />
      </Box>

      <ImagePreview
        isOpen={showImagePreview}
        imageUrl={previewImage}
        onClose={() => setShowImagePreview(false)}
        onConfirm={handleImagePreviewConfirm}
        isLoading={isLoading}
      />

      <LocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onConfirm={handleLocationConfirm}
        isLoading={isLoading}
      />

      <StickerPicker
        isOpen={showStickerPicker}
        onClose={() => setShowStickerPicker(false)}
        onSelectSticker={handleStickerSelect}
      />
    </Sheet>
  );
};

export default ComposerBar;
